import { Controller } from "@hotwired/stimulus"

// Manual "Add charge" form. Two jobs:
//   1. Live "new balance" preview as the operator types the amount, so she sees
//      the consequence before committing (the trust moment).
//   2. Tap-to-fill fee preset chips that set the amount (when known), the
//      description, and a hidden category, marking themselves selected.
// Modeled on revenue_calc_controller.js (same #num / #money helpers).
export default class extends Controller {
  static targets = ["amount", "newBalance", "description", "category", "chip"]
  static values = { currentCents: Number, cardAdjustBps: Number }

  connect() {
    // On an error re-render the hidden category survives — re-light its chip.
    this.#syncSelectedChip()
    this.preview()
  }

  // Recompute the projected balance on every keystroke.
  preview() {
    const entered = this.#num(this.amountTarget)
    const category = this.hasCategoryTarget ? this.categoryTarget.value : ""
    const newCents = this.currentCentsValue + this.#postedCents(entered, category)
    this.newBalanceTarget.textContent = this.#money(newCents / 100)
  }

  // Mirrors ChargesController#late_fee_posted_cents (server-side source of
  // truth — this is a preview only, the trust moment before she submits): a
  // hand-entered LATE FEE on a dual-pricing team posts at the card price, same
  // as rent and the automated stage-ladder fee. Every other charge posts
  // exactly what she typed. cardAdjustBpsValue is 0 whenever the team has dual
  // pricing off, which collapses this to a no-op.
  #postedCents(entered, category) {
    const base = Math.round(entered * 100)
    if (category !== "late_fee" || !this.cardAdjustBpsValue) return base

    return base + Math.round((base * this.cardAdjustBpsValue) / 10000)
  }

  // Fill the form from a preset chip and mark it selected.
  select(event) {
    const chip = event.currentTarget
    const { category, amount, description } = chip.dataset

    if (this.hasCategoryTarget) this.categoryTarget.value = category || ""
    if (description && this.hasDescriptionTarget && !this.descriptionTarget.value) {
      this.descriptionTarget.value = description
    }
    if (amount) {
      this.amountTarget.value = amount
    }

    this.#syncSelectedChip()
    this.amountTarget.focus()
    this.preview()
  }

  #syncSelectedChip() {
    const current = this.hasCategoryTarget ? this.categoryTarget.value : ""
    this.chipTargets.forEach((chip) => {
      this.#setSelected(chip, chip.dataset.category === current && current !== "")
    })
  }

  #setSelected(chip, on) {
    chip.classList.toggle("ring-primary-300", on)
    chip.classList.toggle("bg-primary-50", on)
    chip.classList.toggle("text-primary-700", on)
    chip.classList.toggle("ring-secondary-200", !on)
    chip.setAttribute("aria-pressed", on ? "true" : "false")
  }

  #num(el) {
    const v = parseFloat(el.value)
    return Number.isNaN(v) ? 0 : v
  }

  #money(n) {
    return n.toLocaleString("en-US", { style: "currency", currency: "USD" })
  }
}
